/**
 * FAQ accordion — smooth height + fade animation on open/close.
 * Falls back to native <details> behaviour without JS or with reduced motion.
 */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initItem = (item) => {
    const summary = item.querySelector('.iurop-faq__q');
    const panel = item.querySelector('.iurop-faq__a');
    if (!summary || !panel) return;

    const onEnd = (cb) => {
      const handler = (e) => {
        if (e.propertyName !== 'height') return;
        panel.removeEventListener('transitionend', handler);
        cb();
      };
      panel.addEventListener('transitionend', handler);
    };

    const open = () => {
      item.open = true;
      if (reduceMotion) return;
      item.classList.add('is-animating');
      panel.style.height = '0px';
      panel.style.opacity = '0';
      const target = panel.scrollHeight;
      requestAnimationFrame(() => {
        panel.style.height = target + 'px';
        panel.style.opacity = '1';
      });
      onEnd(() => {
        panel.style.height = 'auto';
        item.classList.remove('is-animating');
      });
    };

    const close = () => {
      if (reduceMotion) { item.open = false; return; }
      item.classList.add('is-animating', 'is-closing');
      panel.style.height = panel.scrollHeight + 'px';
      panel.style.opacity = '1';
      requestAnimationFrame(() => {
        panel.style.height = '0px';
        panel.style.opacity = '0';
      });
      onEnd(() => {
        item.open = false;
        panel.style.height = '';
        panel.style.opacity = '';
        item.classList.remove('is-animating', 'is-closing');
      });
    };

    summary.addEventListener('click', (e) => {
      e.preventDefault();
      if (item.classList.contains('is-animating')) return;
      if (item.open) close(); else open();
    });
  };

  // Combined category-tab + keyword filtering for one FAQ list.
  const initFilters = (list) => {
    const items = Array.from(list.querySelectorAll('.iurop-faq__item'));
    if (!items.length) return;
    const tabs = list.querySelector('.iurop-faq__tabs');
    const search = list.querySelector('[data-faq-search]');
    const empty = list.querySelector('[data-faq-empty]');
    if (!tabs && !search) return;

    let cat = 'all';
    let query = '';
    let animating = false;

    const matches = (item) => {
      if (cat !== 'all' && item.dataset.cat !== cat) return false;
      if (query && (item.textContent || '').toLowerCase().indexOf(query) === -1) return false;
      return true;
    };

    const applyFilter = () => {
      items.forEach((item) => {
        const show = matches(item);
        item.classList.toggle('is-hidden', !show);
        if (!show && item.open) {
          item.open = false;
          const panel = item.querySelector('.iurop-faq__a');
          if (panel) { panel.style.height = ''; panel.style.opacity = ''; }
          item.classList.remove('is-animating', 'is-closing');
        }
      });
      if (empty) empty.hidden = items.some((it) => !it.classList.contains('is-hidden'));
    };

    // Animate the list height (the side image stretches to the same row, so it
    // resizes smoothly along with it) + a fade-in on the now-visible items.
    const applyAnimated = () => {
      if (reduceMotion) { applyFilter(); return; }
      const startH = list.offsetHeight;
      applyFilter();
      const endH = list.offsetHeight;

      list.classList.remove('is-switching');
      void list.offsetWidth;
      list.classList.add('is-switching');
      list.addEventListener('animationend', function clearSwitch() {
        list.classList.remove('is-switching');
        list.removeEventListener('animationend', clearSwitch);
      });

      if (startH === endH) return;

      animating = true;
      list.style.overflow = 'hidden';
      list.style.height = startH + 'px';
      void list.offsetWidth;
      list.style.transition = 'height 0.4s cubic-bezier(0.22, 1, 0.36, 1)';
      requestAnimationFrame(() => { list.style.height = endH + 'px'; });

      const onEnd = (ev) => {
        if (ev.target !== list || ev.propertyName !== 'height') return;
        list.style.height = '';
        list.style.transition = '';
        list.style.overflow = '';
        list.removeEventListener('transitionend', onEnd);
        animating = false;
      };
      list.addEventListener('transitionend', onEnd);
    };

    if (tabs) {
      tabs.addEventListener('click', (e) => {
        const btn = e.target.closest('.iurop-faq__tab');
        if (!btn || btn.classList.contains('is-active') || animating) return;
        cat = btn.dataset.cat;
        tabs.querySelectorAll('.iurop-faq__tab').forEach((b) => {
          const active = b === btn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        applyAnimated();
      });
    }

    if (search) {
      let debounce = null;
      search.addEventListener('input', () => {
        query = search.value.trim().toLowerCase();
        clearTimeout(debounce);
        // Instant filter (no height tween) keeps typing smooth.
        debounce = setTimeout(applyFilter, 120);
      });
    }
  };

  const boot = (root) => {
    const scope = root || document;
    scope.querySelectorAll('.iurop-faq__item').forEach(initItem);
    scope.querySelectorAll('.iurop-faq__list').forEach(initFilters);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
